import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, Link } from "react-router-dom";
import "./blog.css";
import postsData from "../data/blogPosts.json";
import {
  getMenuOffset,
  smoothScrollToElement,
  smoothScrollToPosition,
} from "../utils/scroll";

/* ---------- utils ---------- */
const toSlug = (s = "") =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

// 从 CSS 变量读取固定导航高度，做滚动偏移
const scrollToId = (id) => {
  smoothScrollToElement(id, { menuId: "menu" });
};

const scrollToTop = () => {
  smoothScrollToPosition(0, 820);
};

const sectionLabels = {
  parenting: "Parenting notes",
  teaching: "Learning notes",
  mental: "Caregiver wellbeing",
};

/* ---------- 小卡片 ---------- */
const SmallCard = ({ post, onClick }) => {
  const date = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : null;

  return (
    <article className="post-card" role="button" onClick={onClick}>
      <img
        className="media"
        src={post.image}
        alt={post.title}
        onError={(e) => (e.currentTarget.src = "./img/blog/placeholder.jpg")}
      />
      <div className="body">
        <h4 className="title">{post.title}</h4>
        {post.description && <p className="desc">{post.description}</p>}
        <div className="meta">
          {post.writtenBy && <span>{post.writtenBy}</span>}
          {date && <span>{date}</span>}
        </div>
      </div>
    </article>
  );
};

/* ---------- 主组件 ---------- */
export const Blog = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState("featured"); // ScrollSpy 当前导航

  // 展开状态（默认都收起）
  const [expanded, setExpanded] = useState({
    parenting: false,
    teaching: false,
    mental: false,
  });

  const toggleSection = (key) => {
    setExpanded((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      // 切换后把视图锚定回区块顶部，避免布局变化造成跳动
      setTimeout(() => scrollToId(key), 0);
      return next;
    });
  };

  const VISIBLE_COUNT = 3;

  // 数据：按日期新→旧
  const source = useMemo(() => {
    const raw = Array.isArray(postsData) && postsData.length ? postsData : [];
    const arr = [...raw];
    arr.sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    });
    return arr;
  }, []); // ✅ 不需要 postsData 依赖

  const parenting = source.filter(
    (p) => (p.section || "").toLowerCase() === "parenting"
  );
  const teaching = source.filter(
    (p) => (p.section || "").toLowerCase() === "teaching"
  );
  const mental = source.filter(
    (p) => (p.section || "").toLowerCase() === "mental"
  );
  const featured = source.filter(
    (p) => (p.section || "").toLowerCase() === "featured"
  );

  // 区块 refs（用于 ScrollSpy）
  const parentingRef = useRef(null);
  const teachingRef = useRef(null);
  const mentalRef = useRef(null);

  // ScrollSpy：根据滚动位置高亮对应菜单
  useEffect(() => {
    const sectionIds = ["featured", "parenting", "teaching", "mental"];
    let ticking = false;

    const updateActive = () => {
      ticking = false;
      const offset = getMenuOffset("menu", 10);
      let current = "featured";

      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - offset <= 0) current = id;
      }

      setActive((prev) => (prev === current ? prev : current));
    };

    const handler = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateActive);
    };

    updateActive();
    window.addEventListener("scroll", handler, { passive: true });
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
    };
  }, []);

  // 菜单点击：Home 路由，其他锚点平滑滚动
  const onNavClick = useCallback((e, id) => {
    e.preventDefault();
    setActive(id);
    if (id === "featured") {
      scrollToTop();
      return;
    }
    scrollToId(id);
  }, []);

  // 通用的分区渲染
  const RenderSection = ({ id, title, posts, innerRef }) => {
    const isExpanded = expanded[id];
    const list = isExpanded ? posts : posts.slice(0, VISIBLE_COUNT);
    const showToggle = posts.length > VISIBLE_COUNT;

    return (
      <section id={id} ref={innerRef} className="section-wrap">
        <div className="section-head">
          <div className="section-title-copy">
            <p className="section-kicker blog-section-kicker">
              {sectionLabels[id]}
            </p>
            <h2>{title}</h2>
          </div>
        </div>

        <div className="post-grid">
          {list.map((p) => {
            const slug = p.slug || toSlug(p.title);
            return (
              <SmallCard
                key={slug}
                post={p}
                onClick={() => navigate(`/blog/${slug}`)}
              />
            );
          })}
        </div>

        {showToggle && (
          <div className="section-actions">
            <button className="btn-toggle" onClick={() => toggleSection(id)}>
              {isExpanded ? "Show less" : `Show all ${posts.length} posts`}
            </button>
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="blog">
      {/* NAV（结构不变） */}
      <nav id="menu" className="navbar navbar-default navbar-fixed-top">
        <div className="container">
          <div className="navbar-header">
            <button
              type="button"
              className="navbar-toggle collapsed"
              data-toggle="collapse"
              data-target="#bs-example-navbar-collapse-1"
            >
              <span className="sr-only">Toggle navigation</span>
              <span className="icon-bar"></span>
              <span className="icon-bar"></span>
              <span className="icon-bar"></span>
            </button>

            <Link
              className="navbar-brand page-scroll"
              to="/"
              style={{ display: "flex", alignItems: "center" }}
            >
              <img
                src="./img/cradleai_logo.png"
                alt="CradleAI Logo"
                style={{ height: 40, display: "inline-block" }}
              />
            </Link>
          </div>

          <div
            className="collapse navbar-collapse"
            id="bs-example-navbar-collapse-1"
          >
            <ul className="nav navbar-nav navbar-right">
              <li>
                <Link to="/" className="page-scroll">
                  Home
                </Link>
              </li>
              <li>
                <a
                  href="#featured"
                  className={`page-scroll ${
                    active === "featured" ? "is-active" : ""
                  }`}
                  onClick={(e) => onNavClick(e, "featured")}
                >
                  Featured
                </a>
              </li>
              <li>
                <a
                  href="#parenting"
                  className={`page-scroll ${
                    active === "parenting" ? "is-active" : ""
                  }`}
                  onClick={(e) => onNavClick(e, "parenting")}
                >
                  Parenting Basics
                </a>
              </li>
              <li>
                <a
                  href="#teaching"
                  className={`page-scroll ${
                    active === "teaching" ? "is-active" : ""
                  }`}
                  onClick={(e) => onNavClick(e, "teaching")}
                >
                  Teaching Methods
                </a>
              </li>
              <li>
                <a
                  href="#mental"
                  className={`page-scroll ${
                    active === "mental" ? "is-active" : ""
                  }`}
                  onClick={(e) => onNavClick(e, "mental")}
                >
                  Mental Health
                </a>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Anchor for Home */}
      <div id="page-top"></div>

      {/* ====== Header：左主打 + 右侧 Featured 列表（右侧即 featured 锚点） ====== */}
      <header id="header">
        <div className="intro">
          <div className="hero-wrap">
            {/* 左：取 featured 的最新一篇 */}
            {featured.length > 0 &&
              (() => {
                const main = featured[0];
                const slug = main.slug || toSlug(main.title);
                const mainDate = main.date
                  ? new Date(main.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : null;
                return (
                  <article
                    className="hero-main"
                    onClick={() => navigate(`/blog/${slug}`)}
                    role="button"
                  >
                    <img
                      className="hero-img"
                      src={main.image}
                      alt={main.title}
                      onError={(e) =>
                        (e.currentTarget.src = "./img/blog/placeholder.jpg")
                      }
                    />
                    <div className="hero-body">
                      <p className="section-kicker hero-kicker">
                        Latest insight
                      </p>
                      <h2>{main.title}</h2>
                      {main.description && (
                        <p className="desc">{main.description}</p>
                      )}
                      <div className="meta">
                        {main.writtenBy && <span>{main.writtenBy}</span>}
                        {mainDate && <span>{mainDate}</span>}
                      </div>
                    </div>
                  </article>
                );
              })()}

            {/* 右：Featured 列表 + 锚点 id */}
            <aside className="hero-list" id="featured">
              <div className="hero-list-heading">
                <div className="hero-list-title-group">
                  <p className="section-kicker featured-kicker">
                    Editor's picks
                  </p>
                  <h3 className="list-title">Featured Stories</h3>
                </div>
              </div>
              {(featured.length > 1 ? featured.slice(1, 3) : []).map((p, i) => {
                const slug = p.slug || toSlug(p.title);
                const date = p.date
                  ? new Date(p.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "";
                return (
                  <div
                    key={slug || i}
                    className="item"
                    onClick={() => navigate(`/blog/${slug}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/blog/${slug}`);
                      }
                    }}
                  >
                    <div className="item-copy">
                      <div
                        className="item-title"
                        aria-label={p.title}
                        title={p.title}
                      >
                        {p.title}
                      </div>
                      <div className="item-sub">
                        <span>{p.writtenBy || ""}</span>
                        <span>{date}</span>
                      </div>
                    </div>
                    <span className="item-arrow" aria-hidden="true">
                      →
                    </span>
                  </div>
                );
              })}
            </aside>
          </div>
        </div>
      </header>

      {/* ====== 三个内容分区（与 Header 同宽；默认展示 3 篇，按钮展开/收起） ====== */}
      <RenderSection
        id="parenting"
        innerRef={parentingRef}
        title="Parenting Basics"
        posts={parenting}
      />

      <RenderSection
        id="teaching"
        innerRef={teachingRef}
        title="Teaching Methods"
        posts={teaching}
      />

      <RenderSection
        id="mental"
        innerRef={mentalRef}
        title="Mental Health"
        posts={mental}
      />
    </div>
  );
};
